/**
 * Factory Dashboard Service
 *
 * API client for AI Factory Dashboard endpoints.
 *
 * Part of Story 74: AI Factory Dashboard
 */

import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import type { BotPriorityItem } from '@/components/shared/BotAttentionSection';

// Types
export interface ProductionMetrics {
  currentBatch: {
    id: string;
    status: string;
    progress: number;
    totalProducts: number;
    completedProducts: number;
    startedAt: string;
  } | null;
  thisWeek: {
    productsEnriched: number;
    pagesGenerated: number;
    specsResearched: number;
    validationsCompleted: number;
  };
  lastWeek: {
    productsEnriched: number;
    pagesGenerated: number;
    specsResearched: number;
    validationsCompleted: number;
  };
  trends: {
    productsEnrichedChange: number;
    pagesGeneratedChange: number;
    specsResearchedChange: number;
  };
  dailyProduction: Array<{
    date: string;
    productsEnriched: number;
    pagesGenerated: number;
    specsResearched: number;
  }>;
}

export interface QualityControl {
  overallPassRate: number;
  thisMonth: {
    passed: number;
    flagged: number;
    blocked: number;
  };
  byValidationType: Array<{
    type: string;
    passRate: number;
    count: number;
  }>;
  topRejectionReasons: Array<{
    reason: string;
    count: number;
    examples: string[];
  }>;
  qualityTrend: Array<{
    date: string;
    passRate: number;
  }>;
}

export interface PipelineStatus {
  stages: Array<{
    stage: string;
    count: number;
    percentage: number;
  }>;
  bottleneck: {
    stage: string;
    queueSize: number;
    avgWaitTime: number;
  } | null;
  stuckItems: Array<{
    stage: string;
    count: number;
    oldestDays: number;
  }>;
  avgTimePerStage: Array<{
    stage: string;
    avgHours: number;
    targetHours: number;
  }>;
}

export interface ImpactMetrics {
  metrics: Array<{
    metric: string;
    before: number;
    after: number;
    improvement: number;
    unit: string;
  }>;
  topImprovements: Array<{
    metric: string;
    improvement: number;
  }>;
}

export interface VisibilityMetrics {
  botDiscovery: Array<{
    botType: string;
    visitsLast30d: number;
    pagesVisited: number;
  }>;
  discoveryRate: number;
  avgTimeToFirstCrawl: number;
  trafficLift: {
    enrichedPagesTraffic: number;
    nonEnrichedTraffic: number;
    liftPercent: number;
  };
}

export interface ResearchMetrics {
  successRate: number;
  outcomes: {
    found: number;
    notAvailable: number;
    searching: number;
  };
  sourceEffectiveness: Array<{
    source: string;
    successRate: number;
    count: number;
  }>;
  successTrend: Array<{
    date: string;
    rate: number;
  }>;
}

export interface TimeToValue {
  stages: Array<{
    stage: string;
    avgHours: number;
    targetHours: number;
    status: 'on_target' | 'warning' | 'critical';
  }>;
  totalPipelineTime: number;
  targetPipelineTime: number;
  trend: 'improving' | 'stable' | 'degrading';
}

export interface CumulativeValue {
  lifetime: {
    productsEnriched: number;
    pagesCreated: number;
    specsAdded: number;
    pagesIndexed: number;
    additionalVisits: number;
    estimatedRevenue: number;
  };
  monthlyGrowth: Array<{
    month: string;
    productsEnriched: number;
    pagesCreated: number;
    cumulativeValue: number;
  }>;
  milestones: Array<{
    title: string;
    achievedAt: string;
  }>;
}

// The bot priority item shape is owned by BotAttentionSection, which renders every
// field; /factory/dashboard carries the same fields (BotAttentionPriorityService.js).
export type { BotPriorityItem };

export interface FactoryDashboardData {
  production: ProductionMetrics;
  qualityControl: QualityControl;
  pipeline: PipelineStatus;
  impact: ImpactMetrics;
  visibility: VisibilityMetrics;
  research: ResearchMetrics;
  timeToValue: TimeToValue;
  botAttentionPriority: {
    priorityItems: BotPriorityItem[];
    summary: {
      priorityCount: number;
      nextBatchCount: number;
      optimizedCount: number;
      discoveryIssueCount: number;
      backlogCount: number;
      total: number;
    };
    trendingCategories: Array<{
      category: string;
      botVisits: number;
      trendPct: number;
      contentCoverage: number;
      productCount: number;
    }>;
    aiAssistantFocus: Array<{
      queryPattern: string;
      botVisits: number;
      coverage: number;
      gap: number;
    }>;
    discoveryProblems: Array<{
      productId: string;
      productName: string;
      hasContent: boolean;
      botVisits: number;
      likelyIssue: string;
    }>;
  };
  cumulative: CumulativeValue;
  meta: {
    siteId: string;
    siteName: string;
    generatedAt: string;
    dataFreshness: string;
    isLive: boolean;
  };
}

// Service functions
export async function fetchDashboard(
  siteId: string,
  startDate?: string,
  endDate?: string
): Promise<FactoryDashboardData> {
  const params = new URLSearchParams({ siteId });
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const res = await authFetch(`${API_URL}/factory/dashboard?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch dashboard: ${res.statusText}`);
  return res.json();
}

export async function fetchProduction(
  siteId: string,
  startDate?: string,
  endDate?: string
): Promise<ProductionMetrics> {
  const params = new URLSearchParams({ siteId });
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const res = await authFetch(`${API_URL}/factory/production?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch production: ${res.statusText}`);
  return res.json();
}

export async function fetchQuality(siteId: string): Promise<QualityControl> {
  const res = await authFetch(`${API_URL}/factory/quality?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch quality: ${res.statusText}`);
  return res.json();
}

export async function fetchPipeline(siteId: string): Promise<PipelineStatus> {
  const res = await authFetch(`${API_URL}/factory/pipeline?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch pipeline: ${res.statusText}`);
  return res.json();
}

export async function fetchThroughput(
  siteId: string,
  days: number = 7
): Promise<{
  daily: Array<{
    date: string;
    completed: number;
    failed: number;
    avgCompletionHours: number;
  }>;
  totals: {
    completed: number;
    failed: number;
    avgDaily: number;
    successRate: number;
  };
}> {
  const res = await authFetch(`${API_URL}/factory/throughput?siteId=${siteId}&days=${days}`);
  if (!res.ok) throw new Error(`Failed to fetch throughput: ${res.statusText}`);
  return res.json();
}

export async function fetchBotPriority(
  siteId: string,
  options: { limit?: number; status?: string } = {}
): Promise<BotPriorityItem[]> {
  const params = new URLSearchParams({ siteId });
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.status) params.append('status', options.status);

  const res = await authFetch(`${API_URL}/factory/bot-priority?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch bot priority: ${res.statusText}`);
  return res.json();
}

export async function fetchBotPrioritySummary(siteId: string): Promise<{
  priorityCount: number;
  nextBatchCount: number;
  optimizedCount: number;
  discoveryIssueCount: number;
  backlogCount: number;
  total: number;
}> {
  const res = await authFetch(`${API_URL}/factory/bot-priority/summary?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch bot priority summary: ${res.statusText}`);
  return res.json();
}

export async function fetchTrendingCategories(
  siteId: string,
  limit: number = 10
): Promise<Array<{
  category: string;
  botVisits: number;
  trendPct: number;
  contentCoverage: number;
  productCount: number;
}>> {
  const res = await authFetch(`${API_URL}/factory/bot-priority/trending?siteId=${siteId}&limit=${limit}`);
  if (!res.ok) throw new Error(`Failed to fetch trending categories: ${res.statusText}`);
  return res.json();
}

export async function queueProductsForGeneration(
  siteId: string,
  productIds: string[],
  priority: 'normal' | 'high' = 'normal'
): Promise<{
  queued: number;
  alreadyQueued: number;
  errors: string[];
}> {
  const res = await authFetch(`${API_URL}/factory/queue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, productIds, priority })
  });
  if (!res.ok) throw new Error(`Failed to queue products: ${res.statusText}`);
  return res.json();
}

export async function fetchActiveRuns(siteId: string): Promise<Array<{
  id: string;
  state: string;
  progress: {
    pilot: { total: number; completed: number };
    full: { total: number; completed: number };
  };
  startedAt: string;
  createdBy: string;
}>> {
  const res = await authFetch(`${API_URL}/factory/active-runs?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch active runs: ${res.statusText}`);
  return res.json();
}

export async function fetchStageTimings(siteId: string): Promise<Array<{
  stage: string;
  avgHours: number;
  medianHours: number;
  p95Hours: number;
}>> {
  const res = await authFetch(`${API_URL}/factory/stage-timings?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch stage timings: ${res.statusText}`);
  return res.json();
}

export interface KeywordEnrichmentStatus {
  siteId: string;
  enriched: number;
  pending: number;
  // Optional: rows where the last enrichment attempt wrote an error
  // stub. Surfaced as a red "Errored: N" tile in DemandResearch.
  // Optional because older backend builds don't include it.
  errored?: number;
  total: number;
  coverage: number;
  lastUpdated: string | null;
}

export async function fetchKeywordEnrichmentStatus(siteId: string): Promise<KeywordEnrichmentStatus> {
  const res = await authFetch(`${API_URL}/factory/keyword-enrichment/status?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch keyword enrichment status: ${res.statusText}`);
  return res.json();
}

export async function triggerKeywordEnrichment(
  siteId: string,
  options: { language?: string; location?: string } = {}
): Promise<{
  message: string;
  siteId: string;
  status: string;
  markets?: string;
  note: string;
}> {
  // Build request body - only include language/location if explicitly provided
  // If not provided, backend will enrich ALL configured markets for the site
  const body: { siteId: string; language?: string; location?: string } = { siteId };
  if (options.language) body.language = options.language;
  if (options.location) body.location = options.location;

  const res = await authFetch(`${API_URL}/factory/keyword-enrichment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(`Failed to trigger keyword enrichment: ${res.statusText}`);
  return res.json();
}
